import { Box, Stack } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import {
  Button,
  DataLossPrompt,
  Footer,
  Loader,
  NothingFoundAlert,
  Panel,
  SelectInput,
  SortableList,
  TextInput,
  Typography,
} from 'melp-design/components';
import { ContentLanguage, uppercaseContentLanguages } from 'types/general';
import { useRouteParams } from 'utils/useIdParam';
import { formatLanguage } from 'utils/format';
import { usePredefinedToasts } from 'utils/Toast';
import {
  useAdminCompany,
  useAdminUpdateCompanySettings,
} from 'store/admin-companies';
import { Delete } from '@mui/icons-material';

interface Values {
  accountantEmails: Array<{ email: string }>;
  accountantLanguage: Uppercase<ContentLanguage>;
}

const INITIAL_VALUES: Values = {
  accountantEmails: [{ email: '' }],
  accountantLanguage: 'EN',
};

export const AdminClientCompanyDetails = () => {
  const { t, i18n } = useTranslation();
  const { companyId } = useRouteParams(
    ROUTES.admin.clients.details.companies.details,
  );
  const predefinedToasts = usePredefinedToasts();

  const { data: company, isLoading } = useAdminCompany(companyId, {
    enabled: !!companyId,
  });

  const { mutate: updateCompany, isLoading: isUpdating } =
    useAdminUpdateCompanySettings();

  const {
    reset,
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });
  const {
    fields: accountantEmailsFields,
    append: appendAccountantEmail,
    remove: removeAccountantEmail,
  } = useFieldArray({
    control,
    name: 'accountantEmails',
  });

  useEffect(() => {
    if (company) {
      reset({
        accountantEmails: company.settings.accountantEmails.map((email) => ({
          email,
        })),
        accountantLanguage: company.settings.accountantLanguage,
      });
    }
  }, [company, reset]);

  if (isLoading) return <Loader />;
  if (!company) return <NothingFoundAlert />;

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateCompany(
      {
        companyId,
        settings: {
          accountantEmails: values.accountantEmails.map(({ email }) => email),
          accountantLanguage: values.accountantLanguage,
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
        },
      },
    );
  };

  return (
    <>
      <Stack>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Panel title={t('companies.finances.title')}>
              <Stack gap={2}>
                <Controller
                  name="accountantLanguage"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <SelectInput
                        label={t('companies.accountantLanguage.label')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        options={uppercaseContentLanguages.map((language) => ({
                          label: formatLanguage(i18n.language, language),
                          value: language,
                        }))}
                        ref={ref}
                      />
                    );
                  }}
                />

                <Stack gap={1}>
                  <Typography variant="p1" color="textSecondary">
                    {t('companies.accountantEmail.label')}
                  </Typography>
                  <SortableList
                    key={JSON.stringify(
                      accountantEmailsFields.map((field) => field.id),
                    )}
                    disabled
                    items={accountantEmailsFields.map((field, i) => ({
                      id: field.id,
                      content: (
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="stretch"
                          py={0.5}
                          spacing={2}
                          key={field.id}
                        >
                          <Box flex={1}>
                            <Controller
                              name={`accountantEmails.${i}.email`}
                              control={control}
                              render={({
                                field: { name, value, onChange, ref },
                              }) => (
                                <TextInput
                                  label={t('common.email')}
                                  name={name}
                                  value={value}
                                  onChange={onChange}
                                  type="email"
                                  sx={{ width: '100%' }}
                                  required
                                  ref={ref}
                                />
                              )}
                            />
                          </Box>

                          <Box flexShrink={0}>
                            <Button
                              variant="neutral-outline"
                              size="md"
                              icon={Delete}
                              onClick={() => removeAccountantEmail(i)}
                              disabled={accountantEmailsFields.length <= 1}
                              title={t('common.remove')}
                            />
                          </Box>
                        </Stack>
                      ),
                    }))}
                  />

                  <Box>
                    <Button
                      label={t('common.add')}
                      variant="tertiary"
                      onClick={() => {
                        appendAccountantEmail({ email: '' });
                      }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Panel>
          </Stack>

          <Footer visible={isDirty}>
            <Stack direction="row" justifyContent="end" gap={1}>
              <Button
                label={t('common.cancel')}
                variant="neutral-outline"
                onClick={() => {
                  reset(undefined, { keepDirty: false });
                }}
              />
              <Button
                label={t('common.confirm_changes')}
                type="submit"
                disabled={isUpdating}
              />
            </Stack>
          </Footer>
        </form>
      </Stack>

      <DataLossPrompt when={isDirty} />
    </>
  );
};
